import { AnimatePresence, motion } from 'framer-motion';
import { graphql, Link } from 'gatsby';
import React from 'react';
import styled from 'styled-components';
import Portfolio from '~/components/ContentFactory/Portfolio';
import Service from '~/components/ContentFactory/Service';
import Values from '~/components/ContentFactory/Values';
import Image from '~/components/Dynamic/Image';
import Layout from '~/components/Layout';
import ArrowRight from '~/icons/white-arrow.svg';
import Seo from '~/components/SEO';
import CustomCursor from '~/components/CustomCursor';
import renderLink from '~/utils/render-link';
import useStoryblok from '~/utils/storyblok-service';

const HeroStyled = styled.div`
  &.lang--it,
  &.lang--es,
  &.lang--fr {
    --tw-aspect-w:973;
    --tw-aspect-h:575;
  }
`;

const IndexPage = ({ data, location, pageContext }) => {
  const story = useStoryblok(data.storyblokEntry, location);
  const content = story?.content;
  // console.log(content);

  return (
    <Layout headerColor="black" className="bg-beige" hideFooterCta hideSecondaryFooterCta locale={pageContext.locale}>
      <Seo {...content?.seo} lang={story.lang} pageContext={pageContext} />

      <section className="pt-48 pb-12 -mt-20 text-white bg-black">
        <div className="container ">
          <div className="text-center">
            <h1>
              <HeroStyled className={`aspect-w-717 aspect-h-316 lang--${pageContext.locale.lang}`}>
                <div>
                  <Image blok={content.hero} />
                </div>
              </HeroStyled>
              <span className="hidden">{content.heroText}</span>
            </h1>
            <AnimatePresence>
              <motion.div
                className="w-full h-4 mt-4 bg-blue lg:h-8"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              />
            </AnimatePresence>
          </div>
        </div>

        <div className="mt-0 my-28 lg:mt-28">
          <div className="container">
            <div className="flex flex-col grid-cols-2 lg:grid lg:gap-20">
              <div className="order-1 mt-8 lg:mt-0 lg:order-none">
                <Image blok={content.introImages[0]} />
              </div>
              <div className="mt-24">
                <div className="block mb-10 lg:w-40">
                  <Image blok={content.introText[0].icon} />
                </div>
                <div className="mb-8 md:mb-16 text-[1.125rem]">
                  <p>{content.introText[0].content}</p>
                </div>
                <Image blok={content.introImages[1]} />
              </div>
            </div>
          </div>
        </div>
      </section>

      <Portfolio items={content.portfolioItems} />

      <Values items={content?.stats} />

      <div className="text-black bg-beige py-14">
        <section className="my-20 md:my-28">
          <div className="container">
            <div className="flex flex-wrap justify-between mb-10 lg:flex-nowrap md:mb-24">
              <div className="w-full mb-8 lg:mb-0 lg:w-[48%]">
                <h2 className="mb-6 text-secondary-h1 lg:mb-10">{content?.aboutText[0]?.heading}</h2>
                <div className="inline-block">
                  <Link
                    to={renderLink(content?.aboutText[0]?.button[0]?.button?.cached_url)}
                    className={`c-btn c-btn--primary c-btn--no-icon`}
                  >
                    {content?.aboutText[0]?.button[0]?.title}
                  </Link>
                </div>
              </div>
              <div className="w-full lg:w-[45%] lg:px-8">
                <p>{content.aboutText[0]?.content}</p>
              </div>
            </div>
            <div className="flex flex-wrap justify-between md:flex-nowrap">
              <div className="w-full md:w-[48%]">
                <Image blok={content.aboutImages[0]} />
              </div>
              <div className="w-full md:w-[45%]">
                <Image blok={content.aboutImages[1]} />
              </div>
            </div>
          </div>
        </section>

        <Service heading={content?.servicesHeading} items={content?.services} />

        <div className="mb-0 my-28">
          <div className="container">
            <CustomCursor content={<img src={ArrowRight} alt="" />}>
              <Link
                to={renderLink(content?.portfolio?.cached_url)}
                className="relative block p-8 py-6 overflow-hidden text-center border-2 border-black group"
              >
                <div className="absolute top-0 bottom-0 left-0 right-0 transition-transform duration-300 ease-in-out transform -translate-x-full bg-black group-hover:translate-x-0"></div>
                <div className="relative text-white text-secondary-h1">
                  <div className="pt-4 leading-none" style={{ mixBlendMode: `difference` }}>
                    {content?.ourPortfolio}
                  </div>
                </div>
              </Link>
            </CustomCursor>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default IndexPage;


export const query = graphql`
  query FactoryIndexQuery($id: String!) {
    storyblokEntry(id: { eq: $id }) {
      id
      name
      content
      lang
      slug
      uuid
      full_slug
    }
  }
`;
